import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { buildReportPayload } from "../reports/reportUtils";
import API_URL from "../../config/ApiUrl";

export const getPurchaseOrderReport = createAsyncThunk(
  "purchaseReport/getPurchaseOrderReport",
  async (payload, { rejectWithValue }) => {
    try {
      const body = buildReportPayload(payload);
      const { data } = await axios.post(API_URL.PURCHASE_REPORT.PURCHASE_ORDERS, body);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getPendingPOReport = createAsyncThunk(
  "purchaseReport/getPendingPOReport",
  async (payload, { rejectWithValue }) => {
    try {
      const body = buildReportPayload(payload);
      const { data } = await axios.post(API_URL.PURCHASE_REPORT.PENDING_POS, body);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getGoodsReceiptReport = createAsyncThunk(
  "purchaseReport/getGoodsReceiptReport",
  async (payload, { rejectWithValue }) => {
    try {
      const body = buildReportPayload(payload);
      const { data } = await axios.post(API_URL.PURCHASE_REPORT.GOODS_RECEIPTS, body);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getPurchaseVarianceReport = createAsyncThunk(
  "purchaseReport/getPurchaseVarianceReport",
  async (payload, { rejectWithValue }) => {
    try {
      const body = buildReportPayload(payload);
      const { data } = await axios.post(API_URL.PURCHASE_REPORT.PURCHASE_VARIANCE, body);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Purchase Register: PO-level (aggregated) report with GST breakdown.
// GET with query params, mirroring the Gross Sales Register thunk.
export const getPurchaseRegisterReport = createAsyncThunk(
  "purchaseReport/getPurchaseRegisterReport",
  async (params, { rejectWithValue }) => {
    try {
      const query = new URLSearchParams();
      query.append("FromDate", params.fromDate);
      query.append("ToDate", params.toDate);
      if (params.purchaseOrderNumber) query.append("PurchaseOrderNumber", params.purchaseOrderNumber);
      if (params.vendorIds?.length > 0) params.vendorIds.forEach((id) => query.append("VendorIds", id));
      if (params.locationIds?.length > 0) params.locationIds.forEach((id) => query.append("LocationIds", id));
      if (params.warehouseIds?.length > 0) params.warehouseIds.forEach((id) => query.append("WarehouseIds", id));
      if (params.sourceStateIds?.length > 0) params.sourceStateIds.forEach((id) => query.append("SourceStateIds", id));
      if (params.destinationStateIds?.length > 0) params.destinationStateIds.forEach((id) => query.append("DestinationStateIds", id));
      if (params.externalReferenceNumber) query.append("ExternalReferenceNumber", params.externalReferenceNumber);
      if (params.sortBy) query.append("SortBy", params.sortBy);
      if (params.sortDirection) query.append("SortDirection", params.sortDirection);
      query.append("PageNumber", String(params.pageNumber || 1));
      query.append("PageSize", String(params.pageSize || 50));
      const { data } = await axios.get(`${API_URL.PURCHASE_REPORT.PURCHASE_REGISTER}?${query.toString()}`);
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

// Purchase Monthly: Vendor-wise monthly purchase breakdown
export const getPurchaseMonthlyReport = createAsyncThunk(
  "purchaseReport/getPurchaseMonthlyReport",
  async (params, { rejectWithValue }) => {
    try {
      const query = new URLSearchParams();
      query.append("FromDate", params.fromDate);
      query.append("ToDate", params.toDate);
      if (params.year) query.append("Year", params.year);
      if (params.vendorIds?.length > 0) params.vendorIds.forEach((id) => query.append("VendorIds", id));
      if (params.locationIds?.length > 0) params.locationIds.forEach((id) => query.append("LocationIds", id));
      if (params.warehouseIds?.length > 0) params.warehouseIds.forEach((id) => query.append("WarehouseIds", id));
      if (params.itemBrandIds?.length > 0) params.itemBrandIds.forEach((id) => query.append("ItemBrandIds", id));
      if (params.itemCategoryIds?.length > 0) params.itemCategoryIds.forEach((id) => query.append("ItemCategoryIds", id));
      if (params.sortBy) query.append("SortBy", params.sortBy);
      if (params.sortDirection) query.append("SortDirection", params.sortDirection);
      query.append("PageNumber", String(params.pageNumber || 1));
      query.append("PageSize", String(params.pageSize || 50));
      const { data } = await axios.get(`${API_URL.PURCHASE_REPORT.PURCHASE_MONTHLY}?${query.toString()}`);
      return data;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

const initialState = {
  purchaseOrders: { list: [], total_records: 0 },
  pendingPOs: { list: [], total_records: 0 },
  goodsReceipts: { list: [], total_records: 0 },
  purchaseVariance: { list: [], total_records: 0 },
  purchaseRegister: { items: [], summary: {}, total_records: 0, loading: false },
};

const purchaseReportSlice = createSlice({
  name: "purchaseReport",
  initialState,
  reducers: {
    resetPurchaseRegisterReport: (state) => {
      state.purchaseRegister = initialState.purchaseRegister;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPurchaseRegisterReport.pending, (state) => {
        state.purchaseRegister.loading = true;
      })
      .addCase(getPurchaseRegisterReport.fulfilled, (state, action) => {
        state.purchaseRegister.items = action.payload?.data?.items || [];
        state.purchaseRegister.summary = action.payload?.data?.summary || {};
        state.purchaseRegister.total_records = action.payload?.data?.totalRecords || 0;
        state.purchaseRegister.loading = false;
      })
      .addCase(getPurchaseRegisterReport.rejected, (state) => {
        state.purchaseRegister.loading = false;
      })
      .addCase(getPurchaseOrderReport.fulfilled, (state, action) => {
        state.purchaseOrders.list = action.payload?.data?.list || [];
        state.purchaseOrders.total_records = action.payload?.data?.totalRecords || 0;
      })
      .addCase(getPendingPOReport.fulfilled, (state, action) => {
        state.pendingPOs.list = action.payload?.data?.list || [];
        state.pendingPOs.total_records = action.payload?.data?.totalRecords || 0;
      })
      .addCase(getGoodsReceiptReport.fulfilled, (state, action) => {
        state.goodsReceipts.list = action.payload?.data?.list || [];
        state.goodsReceipts.total_records = action.payload?.data?.totalRecords || 0;
      })
      .addCase(getPurchaseVarianceReport.fulfilled, (state, action) => {
        state.purchaseVariance.list = action.payload?.data?.list || [];
        state.purchaseVariance.total_records = action.payload?.data?.totalRecords || 0;
      });
  },
});

export const { resetPurchaseRegisterReport } = purchaseReportSlice.actions;

export default purchaseReportSlice.reducer;
